/* istanbul ignore file */
import { ReactNode } from 'react';

import type { Orientation } from '@lumapps/lumx/react';
import { Video } from '@lumapps/play/api/types';
import { ReactionCounts, ReactionType } from '@lumapps/reactions-core/api/types';
import { TranslateObject } from '@lumapps/translations';
import type {
    Block,
    BlockImage,
    LegacyWidgetProperties,
    SiteReference,
    UserReference,
} from '@lumapps/widget-base/types';
import { ClientComputedProps } from '@lumapps/widget-base/types/client-computed';

export interface BlockPlayType extends Block {
    type: 'BlockPlay';
}

export interface WidgetPlayVideoIds {
    /**
     * Multi-language video structure with multiple IDs per language:
     * { en: { ids: ['abc', 'def'] }, fr: { ids: ['xyz', '123'] } }
     */
    video?: TranslateObject<
        | {
              ids: Array<Video['id']> | undefined;
          }
        | undefined
    >;
    /**
     * DEPRECATED: We keep the videoId until we are sure that all widgets have
     * been reverted back to video.id
     */
    videoId?: string;
}

export enum MetadataItems {
    author = 'author',
    description = 'description',
    publicationDate = 'publication_date', // snake_case for translation keys
    title = 'title',
    views = 'views',
    social = 'social',
    tags = 'tags',
}

export const AllVideoMetadata = { ...MetadataItems };

export type VisibleMetadata = Array<keyof typeof AllVideoMetadata>;

export interface WidgetPlayPropertiesType extends WidgetPlayVideoIds {
    order?: VisibleMetadata;
    orientation?: Orientation;
    style?: LegacyWidgetProperties['style'];
}

export interface ClientComputedWidgetPlayProps extends ClientComputedProps {
    properties?: WidgetPlayPropertiesType;
}

export interface VisibleMetadataObject {
    author: boolean;
    description: boolean;
    publicationDate: boolean;
    title: boolean;
    views: boolean;
    social: boolean;
    tags?: boolean;
}

export type VisibleElements = (
    | 'AUTHOR'
    | 'CREATED_AT'
    | 'DESCRIPTION'
    | 'DURATION'
    | 'PUBLICATION_INFO'
    | 'THUMBNAIL'
    | 'TITLE'
    | 'VIEWS'
    | 'TAGS'
)[];

/**
 * This interface is our contract with the backend
 */
export interface BlockPlayVideoInterface extends Block {
    /** Video author */
    author: UserReference;

    /** Video creation date */
    createdAt: Video['createdAt'];

    /** Video description */
    description?: string;

    /** Video duration */
    duration: Video['duration'];

    /** Elements to display */
    order: VisibleElements;

    /** Orientation of the component */
    orientation: Uppercase<Orientation>;

    /** Video ID */
    playVideoId: string;

    /** The site to which the video belongs */
    site: SiteReference;

    /** Video thumbnail */
    thumbnail: BlockImage;

    /** Video title */
    title: string;

    /** Widget block type */
    type: 'BlockPlayVideo';

    /** Video views */
    views: number;

    children?: ReactNode;

    currentUserReactions: ReactionType[];

    reactionCounts: ReactionCounts;
    /** Number of comments */
    commentCount?: number;
}
